/**
 * CON-INFRA-001 — Named route-path constants for the Console app.
 *
 * Callers navigate via these helpers instead of inlining `/admin/...`
 * strings so rename refactors stay mechanical:
 *
 *   navigate(adminRoutePaths.products.edit(42));       // → /admin/products/42/edit
 *   <Link to={adminRoutePaths.orders.detail(id)} />;   // → /admin/orders/:id
 *
 * The `pattern` string on each section is the raw React Router path used
 * when *defining* routes (with `:id` placeholders); the parameterised
 * helpers produce concrete URLs for navigation.
 *
 * Keep this file in sync with `router/index.tsx` — adding a route without
 * a matching entry here is a review blocker.
 */
export const adminRoutePaths = {
  root: '/admin',
  dashboard: '/admin',

  /** Public auth routes — rendered outside `AdminLayout`. */
  auth: {
    login: '/login',
  },

  products: {
    list: '/admin/products',
    create: '/admin/products/create',
    editPattern: '/admin/products/:id/edit',
    edit: (id: string | number) => `/admin/products/${id}/edit`,
  },

  categories: {
    list: '/admin/categories',
    create: '/admin/categories/create',
    editPattern: '/admin/categories/:id/edit',
    edit: (id: string | number) => `/admin/categories/${id}/edit`,
  },

  materials: {
    list: '/admin/materials',
    create: '/admin/materials/create',
    editPattern: '/admin/materials/:id/edit',
    edit: (id: string | number) => `/admin/materials/${id}/edit`,
  },

  /** CON-MAT2-003 — Finishes & laminations admin. */
  finishes: {
    list: '/admin/finishes',
    create: '/admin/finishes/create',
    editPattern: '/admin/finishes/:id/edit',
    edit: (id: string | number) => `/admin/finishes/${id}/edit`,
  },

  shapes: {
    list: '/admin/shapes',
    create: '/admin/shapes/create',
    editPattern: '/admin/shapes/:id/edit',
    edit: (id: string | number) => `/admin/shapes/${id}/edit`,
  },

  productTypes: {
    list: '/admin/product-types',
    create: '/admin/product-types/create',
    editPattern: '/admin/product-types/:id/edit',
    edit: (id: string | number) => `/admin/product-types/${id}/edit`,
  },

  taxonomies: {
    list: '/admin/taxonomies',
    create: '/admin/taxonomies/create',
    detailPattern: '/admin/taxonomies/:slug',
    detail: (slug: string) => `/admin/taxonomies/${encodeURIComponent(slug)}`,
    editPattern: '/admin/taxonomies/:slug/edit',
    edit: (slug: string) => `/admin/taxonomies/${encodeURIComponent(slug)}/edit`,
    nodeNewPattern: '/admin/taxonomies/:slug/nodes/new',
    nodeNew: (slug: string) => `/admin/taxonomies/${encodeURIComponent(slug)}/nodes/new`,
    nodeEditPattern: '/admin/taxonomies/:slug/nodes/:nodeId/edit',
    nodeEdit: (slug: string, nodeId: number) => `/admin/taxonomies/${encodeURIComponent(slug)}/nodes/${nodeId}/edit`,
    importPattern: '/admin/taxonomies/:slug/import',
    importNodes: (slug: string) => `/admin/taxonomies/${encodeURIComponent(slug)}/import`,
  },

  vehicles: {
    list: '/admin/vehicles',
    /** CON-VEH-001 — slim Phase 2 create + edit share one page. */
    new: '/admin/vehicles/new',
    detailPattern: '/admin/vehicles/:id',
    detail: (id: string | number) => `/admin/vehicles/${id}`,
    /**
     * Phase 3+ admin surfaces — kept as path constants so the router
     * still resolves the placeholder views without 404ing. Each lands
     * with the matching ticket (vehicle PVO sync, etc).
     */
    sync: '/admin/vehicles/sync',
    /** CON-LAUNCH-004 — Wikipedia dimensions hydration admin screen. */
    dimensions: '/admin/vehicles/dimensions',
    /** CON-LAUNCH-005 — Vehicle dimensions curation queue admin screen. */
    curation: '/admin/vehicles/curation',
    pvoImport: '/admin/vehicles/pvo-import',
    mapping: '/admin/vehicles/mapping',
    editPattern: '/admin/vehicles/:id/edit',
    edit: (id: string | number) => `/admin/vehicles/${id}/edit`,
  },

  orders: {
    list: '/admin/orders',
    detailPattern: '/admin/orders/:id',
    detail: (id: string | number) => `/admin/orders/${id}`,
    /** CON-WRAP-108 — staff-entered wrap order (phone/trade). */
    new: '/admin/orders/new',
    /** CON-SHIPPS-002 — tracking CSV import screen. */
    importTracking: '/admin/orders/import-tracking',
  },

  disputes: {
    list: '/admin/disputes',
    detailPattern: '/admin/disputes/:id',
    detail: (id: string | number) => `/admin/disputes/${id}`,
  },

  quotes: {
    list: '/admin/quotes',
    detailPattern: '/admin/quotes/:id',
    detail: (id: string | number) => `/admin/quotes/${id}`,
  },

  /** CON-CDR-001 list; CON-DSVC-101 added the dedicated detail route. */
  customDesignRequests: {
    list: '/admin/custom-design-requests',
    detailPattern: '/admin/custom-design-requests/:id',
    detail: (id: string | number) => `/admin/custom-design-requests/${id}`,
  },

  prepress: {
    list: '/admin/prepress',
    artworkPattern: '/admin/prepress/:orderId/artwork',
    artwork: (orderId: string | number) => `/admin/prepress/${orderId}/artwork`,
  },

  production: {
    list: '/admin/production',
    queue: '/admin/production/queue',
    shipping: '/admin/production/shipping',
    /** CON-FULFILLMENT-004 — operator-facing job detail page. */
    jobDetailPattern: '/admin/production/jobs/:id',
    jobDetail: (id: string | number) => `/admin/production/jobs/${id}`,
  },

  damageClaims: {
    list: '/admin/damage-claims',
    detailPattern: '/admin/damage-claims/:id',
    detail: (id: string | number) => `/admin/damage-claims/${id}`,
  },

  customers: {
    list: '/admin/customers',
    detailPattern: '/admin/customers/:id',
    detail: (id: string | number) => `/admin/customers/${id}`,
  },

  coupons: {
    list: '/admin/coupons',
    create: '/admin/coupons/new',
    /** CON-PROMO-004 — tabbed detail view (Edit + Usage tabs). */
    detailPattern: '/admin/coupons/:id',
    detail: (id: string | number) => `/admin/coupons/${id}`,
    editPattern: '/admin/coupons/:id/edit',
    edit: (id: string | number) => `/admin/coupons/${id}/edit`,
  },

  promotions: {
    list: '/admin/promotions',
    create: '/admin/promotions/new',
    editPattern: '/admin/promotions/:id/edit',
    edit: (id: string | number) => `/admin/promotions/${id}/edit`,
    automatic: '/admin/promotions/automatic',
    /** CON-PROMO-006 — create/edit form routes for automatic promotions. */
    newPromotion: '/admin/promotions/new',
    editPromotionPattern: '/admin/promotions/:id/edit',
    editPromotion: (id: string | number) => `/admin/promotions/${id}/edit`,
    sales: '/admin/promotions/sales',
  },

  cms: {
    pagesList: '/admin/cms/pages',
    pageCreate: '/admin/cms/pages/create',
    pageEditPattern: '/admin/cms/pages/:id/edit',
    pageEdit: (id: string | number) => `/admin/cms/pages/${id}/edit`,
    faq: '/admin/cms/faq',
    gallery: '/admin/cms/gallery',
    testimonials: '/admin/cms/testimonials',
  },

  /** SEO management routes — CON-SEO-002 / CON-SEO-003. */
  seo: {
    redirects: '/admin/seo/redirects',
  },

  reports: {
    sales: '/admin/reports/sales',
    products: '/admin/reports/products',
    production: '/admin/reports/production',
  },

  settings: {
    general: '/admin/settings/general',
    navigation: '/admin/settings/navigation',
    homepage: '/admin/settings/homepage',
    quotes: '/admin/settings/quotes',
    shipping: '/admin/settings/shipping',
    tax: '/admin/settings/tax',
    emails: '/admin/settings/emails',
    users: '/admin/settings/users',
    api: '/admin/settings/api',
    stripe: '/admin/settings/stripe',
    auditLog: '/admin/settings/audit-log',
    artwork: '/admin/settings/artwork',
    testOrders: '/admin/settings/test-orders',
  },

  /** System-level routes that render outside the admin shell. */
  forbidden: '/403',
} as const;

/** Helper type for callers that want to type-guard against the static keys. */
export type AdminRoutePaths = typeof adminRoutePaths;
